import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getInvoiceDetails, updateDraftInvoice } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import { getTariffHeadsWithDetails } from '../../../../redux/actions/SocietyPortal/TariffHeadAction/TariffHeadAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_INVOICES } from '../../../../Routing/routes';
import { addDaysToDateString, getDueDateError } from '../../../../utils/helpers/universalFunctions';
import { formatIstDate } from '../../../../utils/helpers/istDate';

// Charge/discount heads the user bills. Everything else on the invoice is system derived.
const INVOICE_ITEM_HEAD_TYPES = ['CHARGE', 'DISCOUNT'];

// Heads the backend generates itself (arrears, interest, taxes, round off, adjustment)
const SYSTEM_HEAD_CODES = ['PAR', 'DNR', 'INT', 'CGST', 'SGST', 'IGST', 'RND', 'ADJ'];

const EditInvoice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { invoiceDetails, detailsLoading, updateLoading } = useSelector(state => state.InvoiceReducer || {});
    const { tariffHeadsWithDetails, tariffHeadsWithDetailsLoading } = useSelector(state => state.TariffHeadReducer || {});

    const [formData, setFormData] = useState({ bill_date: '', due_date: '', remarks: '' });
    // Amounts the user can type, keyed by head_id
    const [noneAmounts, setNoneAmounts] = useState({});
    const [prefilled, setPrefilled] = useState(false);

    useEffect(() => {
        if (id) dispatch(getInvoiceDetails({ id }));
    }, [dispatch, id]);

    const isDraft = invoiceDetails?.status === 'DRAFT' && invoiceDetails?.is_carried_forward !== 1;

    // Charge heads for this invoice's type/flat - surfaces NONE heads the draft has no line for
    useEffect(() => {
        if (!invoiceDetails?.invoice_type_id) return;
        dispatch(getTariffHeadsWithDetails({
            society_id: societyId,
            invoice_type_id: invoiceDetails.invoice_type_id,
            ...(invoiceDetails.flat_id ? { flat_id: invoiceDetails.flat_id } : {}),
            ...(invoiceDetails.period_start ? { period_from: String(invoiceDetails.period_start).slice(0, 10) } : {})
        }));
    }, [dispatch, societyId, invoiceDetails?.invoice_type_id, invoiceDetails?.flat_id, invoiceDetails?.period_start]);

    // Prefill the form once the invoice arrives (guarded so typing isn't overwritten on refetch)
    useEffect(() => {
        if (!invoiceDetails || prefilled) return;
        setFormData({
            bill_date: invoiceDetails.bill_date ? String(invoiceDetails.bill_date).slice(0, 10) : '',
            due_date: invoiceDetails.due_date ? String(invoiceDetails.due_date).slice(0, 10) : '',
            remarks: invoiceDetails.remarks || ''
        });
        setPrefilled(true);
    }, [invoiceDetails, prefilled]);

    const tariffHeadsArray = useMemo(() => {
        if (Array.isArray(tariffHeadsWithDetails)) return tariffHeadsWithDetails;
        if (Array.isArray(tariffHeadsWithDetails?.data)) return tariffHeadsWithDetails.data;
        return [];
    }, [tariffHeadsWithDetails]);

    const savedItems = useMemo(() => invoiceDetails?.items || [], [invoiceDetails]);

    // Saved amount per head, so the inputs start from what the draft already holds
    const savedAmountByHead = useMemo(() => {
        const map = {};
        savedItems.forEach(item => {
            if (item.head_id != null) map[item.head_id] = parseFloat(item.base_amount || 0);
        });
        return map;
    }, [savedItems]);

    // The only editable rows: user-entered (NONE) charge/discount heads
    const editableHeads = useMemo(() => tariffHeadsArray.filter(head =>
        INVOICE_ITEM_HEAD_TYPES.includes(head.headType?.type_name)
        && head.calculation_type === 'NONE'
        && !SYSTEM_HEAD_CODES.includes(head.head_code)
    ), [tariffHeadsArray]);

    useEffect(() => {
        if (editableHeads.length === 0) return;
        setNoneAmounts(prev => {
            const next = { ...prev };
            editableHeads.forEach(head => {
                if (next[head.head_id] === undefined) {
                    const saved = savedAmountByHead[head.head_id];
                    next[head.head_id] = saved != null ? String(saved) : '';
                }
            });
            return next;
        });
    }, [editableHeads, savedAmountByHead]);

    const editableHeadIds = useMemo(() => new Set(editableHeads.map(h => h.head_id)), [editableHeads]);

    // Saved rows the user cannot touch, split into auto-calculated charges and system heads
    const autoChargeItems = useMemo(() => savedItems.filter(item =>
        !editableHeadIds.has(item.head_id) && !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code)
    ), [savedItems, editableHeadIds]);

    const systemItems = useMemo(() => savedItems.filter(item =>
        SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code)
    ), [savedItems]);

    const dueDateError = getDueDateError(formData.bill_date, formData.due_date);
    const minDueDate = addDaysToDateString(formData.bill_date, 1);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Drop a due date that is no longer after the invoice date
            ...(name === 'bill_date' && getDueDateError(value, prev.due_date) ? { due_date: '' } : {})
        }));
    };

    const handleAmountChange = (headId, value) => {
        setNoneAmounts(prev => ({ ...prev, [headId]: value }));
    };

    const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (value) => (value ? formatIstDate(value) : '-');

    // Charge subtotal the user controls, before tax/arrears - the server recalculates the rest
    const editableTotal = editableHeads.reduce((sum, head) => sum + (parseFloat(noneAmounts[head.head_id]) || 0), 0);
    const autoChargeTotal = autoChargeItems.reduce((sum, item) => sum + (parseFloat(item.base_amount) || 0), 0);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.bill_date || !formData.due_date) {
            alert('Invoice date and due date are required');
            return;
        }
        if (dueDateError) {
            alert(dueDateError);
            return;
        }

        const items = editableHeads
            .map(head => ({ head_id: head.head_id, amount: parseFloat(noneAmounts[head.head_id]) || 0 }))
            .filter(item => item.amount > 0);

        dispatch(updateDraftInvoice({
            id,
            bill_date: formData.bill_date,
            due_date: formData.due_date,
            remarks: formData.remarks || '',
            items,
            onSuccess: () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES)
        }));
    };

    const handleCancel = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES);

    if (detailsLoading || !invoiceDetails) {
        return (
            <div className="tw-min-h-[60vh] tw-flex tw-items-center tw-justify-center">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-indigo-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">Loading invoice...</p>
                </div>
            </div>
        );
    }

    // Only drafts are editable - anything posted has a journal entry behind it
    if (!isDraft) {
        return (
            <div className="tw-p-6 tw-max-w-3xl tw-mx-auto">
                <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-amber-200 tw-p-8 tw-text-center">
                    <Icon icon="mdi:lock-outline" className="tw-text-4xl tw-text-amber-500 tw-mb-3" />
                    <h2 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-1">This invoice cannot be edited</h2>
                    <p className="tw-text-sm tw-text-gray-500 tw-mb-5">
                        {invoiceDetails.is_carried_forward === 1
                            ? 'Carried forward invoices are locked.'
                            : `Only draft invoices can be edited. This one is ${invoiceDetails.status}.`}
                    </p>
                    <button
                        onClick={handleCancel}
                        className="tw-px-5 tw-py-2.5 tw-bg-indigo-600 tw-text-white tw-rounded-xl tw-text-sm tw-font-semibold hover:tw-bg-indigo-700"
                    >
                        Back to Invoices
                    </button>
                </div>
            </div>
        );
    }

    const documentNumber = invoiceDetails.document_number || `Draft #${invoiceDetails.invoice_id}`;
    const flatLabel = `${invoiceDetails.flat?.wing?.title ? `${invoiceDetails.flat.wing.title} - ` : ''}${invoiceDetails.flat?.flat_number || '-'}`;

    return (
        <div className="tw-p-6 tw-max-w-5xl tw-mx-auto">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
                <button
                    onClick={handleCancel}
                    className="tw-p-1.5 tw-rounded-md tw-text-gray-500 hover:tw-bg-gray-100"
                >
                    <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                </button>
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Edit Draft Invoice</h1>
                    <p className="tw-text-xs tw-text-gray-500">{documentNumber}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Locked details */}
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden">
                    <div className="tw-px-6 tw-py-4 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:lock-outline" className="tw-text-gray-400 tw-text-lg" />
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-700">Locked Details</h2>
                    </div>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-p-6">
                        <div>
                            <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-mb-1">Invoice Type</div>
                            <div className="tw-text-sm tw-font-medium tw-text-gray-800">{invoiceDetails.invoiceType?.invoice_title || '-'}</div>
                        </div>
                        <div>
                            <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-mb-1">Flat</div>
                            <div className="tw-text-sm tw-font-medium tw-text-gray-800">{flatLabel}</div>
                        </div>
                        <div>
                            <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-mb-1">Member</div>
                            <div className="tw-text-sm tw-font-medium tw-text-gray-800">{invoiceDetails.member_details?.name || '-'}</div>
                        </div>
                        <div>
                            <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-mb-1">Billing Period</div>
                            <div className="tw-text-sm tw-font-medium tw-text-gray-800">
                                {invoiceDetails.period || `${formatDate(invoiceDetails.period_start)} - ${formatDate(invoiceDetails.period_end)}`}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Editable invoice details */}
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden">
                    <div className="tw-px-6 tw-py-4 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:calendar-edit" className="tw-text-indigo-500 tw-text-lg" />
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-700">Invoice Details</h2>
                    </div>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5 tw-p-6">
                        <div>
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                Invoice Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="bill_date"
                                value={formData.bill_date}
                                onChange={handleChange}
                                className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">
                                Due Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="due_date"
                                value={formData.due_date}
                                min={minDueDate || undefined}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 ${dueDateError ? 'tw-border-red-400 focus:tw-ring-red-500 focus:tw-border-red-500' : 'tw-border-gray-300 focus:tw-ring-indigo-500 focus:tw-border-indigo-500'}`}
                                required
                            />
                            {dueDateError && (
                                <p className="tw-mt-1.5 tw-text-xs tw-text-red-600">{dueDateError}</p>
                            )}
                        </div>
                        <div className="md:tw-col-span-2">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1.5">Remarks</label>
                            <textarea
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                rows={3}
                                className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
                                placeholder="Notes printed on the invoice"
                            />
                        </div>
                    </div>
                </div>

                {/* Editable charges */}
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden">
                    <div className="tw-px-6 tw-py-4 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <h2 className="tw-text-sm tw-font-bold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:format-list-bulleted" className="tw-text-indigo-500 tw-text-lg" />
                            Charges You Can Edit
                        </h2>
                        {tariffHeadsWithDetailsLoading && (
                            <span className="tw-text-xs tw-font-medium tw-text-indigo-600 tw-flex tw-items-center tw-gap-2">
                                <div className="tw-animate-spin tw-rounded-full tw-h-3.5 tw-w-3.5 tw-border-b-2 tw-border-indigo-600"></div>
                                Loading heads...
                            </span>
                        )}
                    </div>

                    {editableHeads.length === 0 ? (
                        <div className="tw-p-10 tw-text-center tw-text-gray-500">
                            <Icon icon="mdi:information-outline" className="tw-text-3xl tw-mb-2 tw-text-gray-300" />
                            <p className="tw-text-sm">This invoice type has no manually entered charge heads.</p>
                        </div>
                    ) : (
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-sm">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                        <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider">Charge Head</th>
                                        <th className="tw-text-right tw-px-6 tw-py-3 tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-56">Amount (₹)</th>
                                    </tr>
                                </thead>
                                <tbody className="tw-divide-y tw-divide-gray-100">
                                    {editableHeads.map(head => (
                                        <tr key={head.head_id} className="hover:tw-bg-indigo-50/30 tw-transition-colors">
                                            <td className="tw-px-6 tw-py-4">
                                                <div className="tw-font-semibold tw-text-gray-900">{head.head_name}</div>
                                                <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{head.head_code}</div>
                                            </td>
                                            <td className="tw-px-6 tw-py-4">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={noneAmounts[head.head_id] ?? ''}
                                                    onChange={(e) => handleAmountChange(head.head_id, e.target.value)}
                                                    placeholder="0.00"
                                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-text-right focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="tw-bg-gray-50">
                                        <td className="tw-px-6 tw-py-4 tw-text-right tw-font-bold tw-text-gray-700 tw-border-t tw-border-gray-200">Entered Total</td>
                                        <td className="tw-px-6 tw-py-4 tw-text-right tw-font-bold tw-text-indigo-600 tw-border-t tw-border-gray-200">
                                            {formatCurrency(editableTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                {/* Auto-calculated charges */}
                {autoChargeItems.length > 0 && (
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden">
                        <div className="tw-px-6 tw-py-4 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:calculator-variant" className="tw-text-sky-500 tw-text-lg" />
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-700">Auto-Calculated Charges</h2>
                            <span className="tw-text-xs tw-text-gray-400">recalculated on save</span>
                        </div>
                        <div className="tw-divide-y tw-divide-gray-100">
                            {autoChargeItems.map(item => (
                                <div key={item.item_id || item.head_id} className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3">
                                    <div>
                                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">{item.tariffHead?.head_name || item.description}</div>
                                        <div className="tw-text-xs tw-text-gray-500">{item.tariffHead?.head_code}</div>
                                    </div>
                                    <div className="tw-text-sm tw-font-semibold tw-text-gray-700">{formatCurrency(item.base_amount)}</div>
                                </div>
                            ))}
                            <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3 tw-bg-gray-50">
                                <span className="tw-text-sm tw-font-bold tw-text-gray-700">Subtotal</span>
                                <span className="tw-text-sm tw-font-bold tw-text-gray-800">{formatCurrency(autoChargeTotal)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* System heads */}
                {systemItems.length > 0 && (
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6 tw-overflow-hidden">
                        <div className="tw-px-6 tw-py-4 tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:cog-outline" className="tw-text-gray-400 tw-text-lg" />
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-700">Arrears, Interest &amp; Taxes</h2>
                            <span className="tw-text-xs tw-text-gray-400">system calculated</span>
                        </div>
                        <div className="tw-divide-y tw-divide-gray-100">
                            {systemItems.map(item => (
                                <div key={item.item_id || item.head_id} className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3">
                                    <div>
                                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">{item.tariffHead?.head_name || item.description}</div>
                                        <div className="tw-text-xs tw-text-gray-500">{item.tariffHead?.head_code}</div>
                                    </div>
                                    <div className="tw-text-sm tw-font-semibold tw-text-gray-700">{formatCurrency(item.base_amount)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Totals + actions */}
                <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-p-6">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <div>
                            <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-gray-400">Current Net Payable</div>
                            <div className="tw-text-2xl tw-font-bold tw-text-indigo-600">{formatCurrency(invoiceDetails.net_payable)}</div>
                        </div>
                        <p className="tw-text-xs tw-text-gray-500 tw-max-w-sm tw-text-right">
                            GST, round off and net payable are recalculated by the server when you save.
                        </p>
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-gray-100 tw-pt-5">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="tw-px-6 tw-py-2.5 tw-bg-white tw-border tw-border-gray-300 tw-rounded-xl tw-text-sm tw-font-semibold tw-text-gray-700 hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={updateLoading || !!dueDateError}
                            className="tw-flex tw-items-center tw-gap-2 tw-px-7 tw-py-2.5 tw-bg-indigo-600 tw-text-white tw-rounded-xl tw-text-sm tw-font-bold hover:tw-bg-indigo-700 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                        >
                            {updateLoading ? (
                                <>
                                    <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Icon icon="mdi:content-save-outline" className="tw-text-lg" /> Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditInvoice;
